import { createClient, type Provider, type SupabaseClient } from '@supabase/supabase-js';
import { runtimeConfig, isAuthConfigured } from './runtimeConfig';

// The Supabase Auth (GoTrue) client. Only used for authentication — the app's data all
// goes through our own API (never the Supabase data client). The access token GoTrue
// issues is sent to our API as a Bearer; our API verifies it (via the project JWKS for
// GoTrue tokens, HS256 for the ones we mint) and resolves the tenant from the user's record.
//
// PKCE + detectSessionInUrl handle the OAuth/magic-link redirect back to the app;
// persistSession + autoRefreshToken keep the session alive across reloads and refresh the
// token before it expires (our API always sees a fresh token).

// The only vendor type the rest of the SPA may see: the OAuth provider id passed to
// signInWithOAuth. Re-exported here so screens never import '@supabase/supabase-js'
// themselves (lint: hf/no-vendor-import-outside-integration).
export type { Provider as OAuthProvider } from '@supabase/supabase-js';

let client: SupabaseClient | null = null;

export function getSupabase(): SupabaseClient {
  if (!client) {
    if (!isAuthConfigured) {
      throw new Error('Supabase auth is not configured (missing SUPABASE_URL / SUPABASE_ANON_KEY).');
    }
    client = createClient(runtimeConfig.supabaseUrl, runtimeConfig.supabaseAnonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        flowType: 'pkce',
      },
    });
  }
  return client;
}

/** OAuth provider ids, re-exported so pages never import the vendor SDK themselves. */
export type { Provider };

/**
 * GoTrue's TOTP second factor (supabase-js `auth.mfa`), so screens never import the SDK: the
 * factors, enrolment (QR code + secret), verification (the session becomes aal2), removal.
 */
export const gotrueTotp = {
  async factors(): Promise<{ id: string; status: string }[]> {
    const { data, error } = await getSupabase().auth.mfa.listFactors();
    if (error) throw error;
    return (data?.totp ?? []).map((f) => ({ id: f.id, status: f.status }));
  },
  async enroll(): Promise<{ id: string; qrCode: string; secret: string; uri: string }> {
    const { data, error } = await getSupabase().auth.mfa.enroll({ factorType: 'totp', friendlyName: `Authenticator ${new Date().toISOString().slice(0, 10)}` });
    if (error) throw error;
    return { id: data.id, qrCode: data.totp.qr_code, secret: data.totp.secret, uri: data.totp.uri };
  },
  async verify(factorId: string, code: string): Promise<void> {
    const { error } = await getSupabase().auth.mfa.challengeAndVerify({ factorId, code });
    if (error) throw error;
  },
  async unenroll(factorId: string): Promise<void> {
    const { error } = await getSupabase().auth.mfa.unenroll({ factorId });
    if (error) throw error;
  },
};
